/**
 * Reusable skeleton components for charts
 * Eliminates duplication across 6+ skeleton files
 * Supports Bloomberg terminal styling via useThemeConfig
 */

'use client';

import { useSyncExternalStore } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useThemeConfig } from '@/hooks/use-theme-config';
import { cn } from '@/lib/utils';

/**
 * Hydration-safe mounted flag: server renders `false`, client commits `true`
 * in a single pass (no post-mount re-render flash).
 */
const emptySubscribe = () => () => {};
function useMounted() {
  return useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false
  );
}

/**
 * Bar chart skeleton - reusable for any bar chart
 */
export interface BarChartSkeletonProps {
  barCount?: number;
  heights?: number[];
  showLabels?: boolean;
  className?: string;
}

export function BarChartSkeleton({
  barCount = 8,
  heights,
  showLabels = true,
  className = 'h-64'
}: BarChartSkeletonProps) {
  const defaultHeights = heights || Array.from({ length: barCount }, () => 30 + Math.random() * 60);

  return (
    <div className={className}>
      <div className="h-full flex items-end justify-between gap-2 px-2 pb-2">
        {defaultHeights.map((height, i) => (
          <div key={`${height}-${i}`} className="flex-1 flex flex-col items-center gap-1">
            <Skeleton
              className="w-full rounded-t-sm"
              style={{ height: `${height}%` }}
            />
            {showLabels && <Skeleton className="h-3 w-10" />}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Donut chart skeleton with center content
 */
export interface DonutChartSkeletonProps {
  size?: 'sm' | 'md' | 'lg';
  showCenter?: boolean;
  className?: string;
}

const DONUT_SIZES = {
  sm: 'h-32 w-32',
  md: 'h-44 w-44',
  lg: 'h-56 w-56',
};

export function DonutChartSkeleton({
  size = 'md',
  showCenter = true,
  className = 'h-64'
}: DonutChartSkeletonProps) {
  return (
    <div className={`${className} w-full -mx-2 relative`}>
      <div className="absolute inset-0 flex items-center justify-center">
        <Skeleton className={`${DONUT_SIZES[size]} rounded-full`} />
      </div>

      {showCenter && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <Skeleton className="h-8 w-8 rounded-lg mb-1" />
          <Skeleton className="h-7 w-20 mb-1" />
          <Skeleton className="h-3 w-16" />
        </div>
      )}
    </div>
  );
}

/**
 * Legend skeleton - reusable for chart legends
 */
export interface LegendSkeletonProps {
  itemCount?: number;
  layout?: 'horizontal' | 'vertical';
  showIcon?: boolean;
  className?: string;
}

export function LegendSkeleton({
  itemCount = 5,
  layout = 'vertical',
  showIcon = true,
  className
}: LegendSkeletonProps) {
  const { isTerminal } = useThemeConfig();

  // Hydration-safe pattern
  const mounted = useMounted();

  const showTerminal = mounted && isTerminal;

  const containerClass = layout === 'horizontal'
    ? 'flex items-center justify-center gap-3 flex-wrap'
    : 'space-y-2';

  return (
    <div className={`${containerClass} ${className || ''}`}>
      {Array.from({ length: itemCount }).map((_, i) => (
        <div
          key={i}
          className={cn(
            layout === 'horizontal'
              ? 'flex items-center gap-1.5 px-2 py-1 rounded-md border'
              : 'flex items-center justify-between p-2 rounded-lg',
            // Terminal mode container styling
            showTerminal
              ? 'bg-black/50 border-emerald-500/20'
              : 'bg-muted/30 border-border/50'
          )}
        >
          <div className="flex items-center gap-3 overflow-hidden">
            <Skeleton className="w-2 h-2 rounded-full shrink-0" />
            {showIcon && layout === 'vertical' && (
              <Skeleton className="w-6 h-6 rounded-md shrink-0" />
            )}
            <Skeleton className="h-4 w-24" />
          </div>
          {layout === 'vertical' && (
            <div className="text-right shrink-0">
              <Skeleton className="h-4 w-16 mb-0.5" />
              <Skeleton className="h-3 w-10" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Scatter plot skeleton - for bubble charts
 */
export interface ScatterPlotSkeletonProps {
  bubbleCount?: number;
  className?: string;
}

const SCATTER_POSITIONS = [
  { left: '15%', top: '20%', size: 8 },
  { left: '30%', top: '40%', size: 12 },
  { left: '50%', top: '30%', size: 10 },
  { left: '65%', top: '50%', size: 14 },
  { left: '80%', top: '25%', size: 9 },
  { left: '25%', top: '60%', size: 7 },
  { left: '70%', top: '70%', size: 11 },
];

export function ScatterPlotSkeleton({
  bubbleCount = 7,
  className = 'h-96'
}: ScatterPlotSkeletonProps) {
  const { isTerminal } = useThemeConfig();

  // Hydration-safe pattern
  const mounted = useMounted();

  const showTerminal = mounted && isTerminal;

  return (
    <div className={`${className} -mx-2 relative`}>
      {/* Grid lines - terminal mode uses emerald */}
      <div className="absolute inset-0 flex flex-col justify-between py-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={cn(
              "h-px",
              showTerminal ? "bg-emerald-500/10" : "bg-border/20"
            )}
          />
        ))}
      </div>
      <div className="absolute inset-0 flex justify-between px-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={cn(
              "w-px",
              showTerminal ? "bg-emerald-500/10" : "bg-border/20"
            )}
          />
        ))}
      </div>

      {/* Bubble skeletons */}
      {SCATTER_POSITIONS.slice(0, bubbleCount).map((pos) => (
        <div key={`${pos.left}-${pos.top}`} className="absolute" style={{ left: pos.left, top: pos.top }}>
          <Skeleton className={`h-${pos.size} w-${pos.size} rounded-full`} />
        </div>
      ))}
    </div>
  );
}

/**
 * Metric card skeleton - for HHI scores, KPIs, etc.
 */
export interface MetricCardSkeletonProps {
  count?: number;
  cols?: 2 | 3 | 4;
}

export function MetricCardSkeleton({ count = 2, cols = 2 }: MetricCardSkeletonProps) {
  const { isTerminal, hasGlow } = useThemeConfig();

  // Hydration-safe pattern
  const mounted = useMounted();

  const showTerminal = mounted && isTerminal;
  const showGlow = mounted && hasGlow;

  const gridClass = `grid grid-cols-${cols} gap-2`;

  return (
    <div className={gridClass}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "relative overflow-hidden rounded-lg border p-3",
            showTerminal
              ? "bg-black border-emerald-500/30"
              : "border-border/50 bg-gradient-to-br from-muted/30 to-muted/10",
            showGlow && "shadow-[0_0_8px_rgba(16,185,129,0.1)]"
          )}
        >
          {/* Terminal mode corner accents */}
          {showTerminal && (
            <>
              <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-emerald-500/50" />
              <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-emerald-500/50" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-emerald-500/50" />
              <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-emerald-500/50" />
            </>
          )}

          <div className={cn(
            "text-[10px] font-medium mb-1 uppercase tracking-wider",
            showTerminal ? "text-emerald-500/60" : "text-muted-foreground"
          )}>
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-7 w-20" />

          {/* Gradient accent - different for terminal mode */}
          <div className={cn(
            "absolute top-0 right-0 w-16 h-16 rounded-bl-full",
            showTerminal
              ? "bg-gradient-to-br from-emerald-500/5 to-transparent"
              : "bg-gradient-to-br from-primary/5 to-transparent"
          )} />
        </div>
      ))}
    </div>
  );
}

